import React, { useEffect, useState } from "react";
import { Box, SelectChangeEvent } from "@mui/material";
import { Grid } from "@material-ui/core";
import CustomModal from "../../components/common/customModal";
import CustomInput from "../../components/common/inputField";
import LogoUpload from "../../components/common/logoUpload";
import adminServices from "../../services/adminServices";
import { showToast } from "../../components/common/toastNotification";
import { TablePolicyFormData } from "../../types/adminSection";
import AddSearchDropdown from "../../components/addAndSeachSelect";
//import { useAppSelector } from "../../globalState/hooks";
//import { LoginRootState } from "../../types/login";
import globalServices from "../../services/globalServices";
import CustomSelect from "../../components/common/customSelect";

interface PolicyModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  policyUpdateData?: TablePolicyFormData;
}
interface PolicyTypeData {
  id: number;
  policyType: string;
  country: string;
}
const PolicyModal: React.FC<PolicyModalProps> = ({
  open,
  onClose,
  onSave,
  policyUpdateData,
}) => {
  const [btnLoading, setBtnLoading] = useState(false);
  const [formData, setFormData] = useState<TablePolicyFormData>({
    id: null,
    country: "",
    policyType: "",
    documentName: "",
    docUrl: "",
    policyDocFile: null,
  });
  const [formErrors, setFormErrors] = useState<TablePolicyFormData>({
    country: "",
    policyType: "",
    documentName: "",
    docUrl: "",
  });
  const [policyTypeData, setPolicyTypeData] = useState<PolicyTypeData[]>([]);
  useEffect(() => {
    if (policyUpdateData) {
      setFormData({
        id: policyUpdateData.id || null,
        country: policyUpdateData.country || "",
        policyType: policyUpdateData.policyType || "",
        documentName: policyUpdateData.documentName || "",
        docUrl: policyUpdateData.docUrl || "",
      });
    }
  }, [policyUpdateData]);

  useEffect(() => {
    const c = formData.country?.trim();
    if (!c) return;
    const fetchPolicyTypeData = async () => {
      try {
        const res = await globalServices.getPolicyData(c);
        if (res?.data?.success) {
          setPolicyTypeData(res.data.data);
        } else {
          showToast(
            res?.data?.message || "Failed to load policy types",
            "error"
          );
        }
      } catch (error) {
        console.error("Error while fetching policy data:", error);
        showToast("Something went wrong while fetching policy types.", "error");
      }
    };

    fetchPolicyTypeData();
  }, [formData.country]);

  const handleCountryOnChange = (
    e: SelectChangeEvent<string | number> | string
  ) => {
    const next = typeof e === "string" ? e : String(e.target.value);

    setFormData((prev) => ({ ...prev, country: next, policyType: "" }));
    setFormErrors((prev) => ({ ...prev, country: "" }));
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, policyDocFile: file }));
  };
  const validateForm = () => {
    const errors: Partial<TablePolicyFormData> = {};
    if (!formData.country) {
      errors.country = "Country is required";
    }
    if (!formData.policyType) {
      errors.policyType = "Policy Type is required";
    }
    if (!formData.documentName || formData.documentName.trim() === "") {
      errors.documentName = "Document Name is required";
    }
    const docUrl = formData.docUrl?.trim() || "";
    if (!docUrl && !formData.policyDocFile) {
      errors.docUrl = "Kindly upload the policy file";
      showToast(errors.docUrl, "error");
    }
    if (formData.policyDocFile) {
      const file = formData.policyDocFile;
      const allowedExtensions = [".pdf", ".doc", ".docx"];
      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      if (!fileExtension || !allowedExtensions.includes(`.${fileExtension}`)) {
        errors.docUrl = "File must be a .pdf, .doc, or .docx";
        showToast(errors.docUrl, "error");
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleSaveData = async () => {
    if (!validateForm()) return;
    try {
      setBtnLoading(true);
      const { id, country, policyType, documentName, policyDocFile, docUrl } =
        formData;

      const formDataToSend = new FormData();
      formDataToSend.append("Id", id?.toString() ?? "0");
      formDataToSend.append("Country", country?.trim() ?? "");
      formDataToSend.append("PolicyType", policyType?.trim() ?? "");
      formDataToSend.append("DocumentName", documentName?.trim() ?? "");

      if (policyDocFile instanceof File) {
        formDataToSend.append("PolicyDocFile", policyDocFile);
        formDataToSend.append("DocUrl", "");
      } else if (docUrl?.trim()) {
        formDataToSend.append("DocUrl", docUrl.trim());
        formDataToSend.append("PolicyDocFile", "");
      }
      const apiCall = formData.id
        ? adminServices.updatePolicyDataAPI(formDataToSend)
        : adminServices.addPolicyDataAPI(formDataToSend);
      await apiCall
        .then((response) => {
          if (response && response.data) {
            showToast(response.data.message, "success");
            setPolicyTypeData([]);
            onSave();
            onClose();
            handleClosePolicyModal;
          } else {
            showToast(response?.data?.message, "error");
          }
        })
        .catch((error) => {
          console.error("Error", error);
        })
        .finally(() => {
          setBtnLoading(false);
        });
    } catch (error) {
      console.error("Error saving space data:", error);
    }
  };
  const handleClosePolicyModal = () => {
    setFormData({
      id: null,
      country: "",
      policyType: "",
      documentName: "",
      docUrl: "",
      policyDocFile: null,
    });
    setFormErrors({
      country: "",
      policyType: "",
      documentName: "",
    });
    setPolicyTypeData([]);
    onClose();
  };
  const handleTypeChange = (e: SelectChangeEvent<string | number>) => {
    setFormData((prev) => ({
      ...prev,
      policyType: String(e.target.value),
    }));
    setFormErrors((prev) => ({ ...prev, policyType: "" }));
  };
  const handleTypeUpdate = (
    updatedList: { label: string; value: string | number }[]
  ) => {
    if (!updatedList.length) return;

    const latestItem = updatedList[updatedList.length - 1];

    const maybeNum =
      typeof latestItem.value === "number"
        ? latestItem.value
        : Number(latestItem.value);
    const idNum = Number.isFinite(maybeNum) ? maybeNum : Date.now();

    setPolicyTypeData((prev) => [
      ...prev,
      {
        id: idNum,
        policyType: latestItem.label,
        country: formData.country || "",
      },
    ]);
    setFormData((prev) => ({
      ...prev,
      policyType: latestItem.label,
    }));

    setFormErrors((prev) => ({ ...prev, policyType: "" }));
  };

  return (
    <CustomModal
      open={open}
      handleClose={handleClosePolicyModal}
      title={
        formData.id ? "Edit Policies & Procedures" : "Add Policies & Procedures"
      }
      onSave={handleSaveData}
      btnLoading={btnLoading}
    >
      <Box mt={0}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CustomSelect
              name="country"
              label="Country"
              placeHolder="Select Country"
              value={formData.country || ""}
              onChange={handleCountryOnChange}
              options={[
                { label: "India", value: "India" },
                { label: "UK", value: "UK" },
              ]}
              error={formErrors.country}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <AddSearchDropdown
              options={
                policyTypeData.length > 0
                  ? policyTypeData.map((t) => ({
                      label: t.policyType,
                      value: t.policyType,
                    }))
                  : []
              }
              label="Policy Type"
              value={formData.policyType || ""}
              placeHolder="Select type"
              onChange={handleTypeChange}
              error={formErrors.policyType}
              required
              onUpdateTypeList={handleTypeUpdate}
              buttonSection={false}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              label="Document Name"
              type="text"
              name="documentName"
              placeholder="Enter Document Name"
              value={formData.documentName}
              onChange={handleChange}
              error={formErrors.documentName}
              required
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <LogoUpload
              label="File Upload (PDF/DOC)"
              onChange={handleFileUpload}
              fileName={
                formData.policyDocFile instanceof File
                  ? formData.policyDocFile.name
                  : ""
              }
              required
            />
          </Grid>
          <Grid item xs={12} md={7}>
            <CustomInput
              label=""
              type="text"
              name="docUrl"
              placeholder="Enter URL"
              value={formData.docUrl || ""}
              onChange={handleChange}
              readOnly
            />
          </Grid>
        </Grid>
      </Box>
    </CustomModal>
  );
};

export default PolicyModal;
